import axios from 'axios'
import zxcvbn from '@zxcvbn-ts/core'
import { useMemo } from 'react'
import { NavigateFunction } from 'react-router-dom'
import { ApolloClient } from '@apollo/client'
import { message } from 'antd'
import { PASSWORD_MAX, PASSWORD_MIN } from '@User/ResetPasswordPage/constants'
import { Dispatch } from 'redux'

import {
    formatAuthResponse,
    GRAPHQL_API_REST_BASE,
    setAuth,
    setAuthError,
} from '~/redux-modules/auth'
import { SUCCESS } from '~/redux-modules/identity'
import { setUserContext } from '~/sentry'
import { trackIdentify } from '~/lib/analytics/actions/identify'
import getColor from '~/lib/getColor'
import { CurrentUserIdentityDocument } from '~/gql/graphql'

import {
    FIRST_NAME,
    LAST_NAME,
    PASSWORD,
    EMAIL,
    PHONE_NUMBER,
    LOCATION,
    COMPANY,
    SUBSCRIBE,
    BUSINESS_ACCOUNT_NAME,
    BUSINESS_ACCOUNT_RECIPIENT_EMAIL,
    BUSINESS_ACCOUNT_SENDER_NAME,
    BUSINESS_ACCOUNT_SENDER_EMAIL,
    BUSINESS_ACCOUNT_INVITE_TOKEN,
    BUSINESS_ACCOUNT_EXPIRED_INVITE_TOKEN_ERROR,
    BUSINESS_ACCOUNT_INVITE_EXPIRE_DATE,
    BUSINESS_ACCOUNT_EXPIRED_INVITATION_URL,
    SignupFormValues,
    QUERY_STRING,
    PAGE_URL,
    ORIGINAL_REFERRER,
} from './constants'

type ApolloClientType = ApolloClient<object>

type SignUpPostBodyType = {
    email: string
    password: string
    firstName: string
    lastName: string
    company: string
    phoneNumber: string | null
    state: string
    subscribe: boolean
    businessAccountInviteToken: string | null
    [QUERY_STRING]: string
    [PAGE_URL]: string
    [ORIGINAL_REFERRER]: string
}

export const REQUIRED_FIELDS = [
    FIRST_NAME,
    LAST_NAME,
    PASSWORD,
    PHONE_NUMBER,
    EMAIL,
    LOCATION,
]

export const handleSubmit = async ({
    values,
    setLoading,
    handleSignUp,
    dispatch,
    navigate,
    client,
    businessAccountInviteToken,
}: {
    values: SignupFormValues
    setLoading: (loadingStatus: boolean) => void
    handleSignUp: Function
    dispatch: Dispatch
    navigate: NavigateFunction
    client: ApolloClientType
    businessAccountInviteToken: string | null
}) => {
    const decodedQueryString = decodeURIComponent(
        new URLSearchParams(location.search).toString(),
    )
    const pageUrl = window.location.origin + window.location.pathname

    setLoading(true)
    try {
        const postBody: SignUpPostBodyType = {
            email: values[EMAIL],
            password: values[PASSWORD],
            firstName: values[FIRST_NAME],
            lastName: values[LAST_NAME],
            company: values[COMPANY],
            phoneNumber: values[PHONE_NUMBER] || '5555555555',
            state: values[LOCATION],
            subscribe: (values[SUBSCRIBE] || false) as boolean,
            businessAccountInviteToken,
            [QUERY_STRING]: decodedQueryString,
            [PAGE_URL]: pageUrl,
            [ORIGINAL_REFERRER]: document.referrer,
        }

        const signUpResponse = await axios.post(
            `${GRAPHQL_API_REST_BASE}/signup`,
            postBody,
            { withCredentials: true },
        )

        if (signUpResponse?.data?.error) {
            throw signUpResponse.data.error
        }

        if (
            signUpResponse?.data?.authVersion === 2 &&
            signUpResponse?.data?.status === 'VERIFICATION_SENT'
        ) {
            /**
             * VERIFICATION_SENT if email has been sent
             * VERIFIED if a business account invite was successfully claimed during signup
             */
            navigate(`/verifyEmail`)
            return
        }

        const {
            data: signUpResponseData,
            data: {
                user: { id },
                auth: { idToken },
            },
        } = signUpResponse

        const userIdentityResponse = await client.query({
            query: CurrentUserIdentityDocument,
        })

        if (userIdentityResponse?.data?.error) {
            throw userIdentityResponse.data.error
        }

        const { data: userIdentityData } = userIdentityResponse
        const identity = formatAuthResponse(userIdentityData, idToken)
        const action = { type: SUCCESS, identity }

        dispatch(setAuth(signUpResponseData))
        setUserContext({ email: identity.email, id: identity.id })
        dispatch(trackIdentify(action, identity.id, identity))
        handleSignUp(id)
        setLoading(false)
    } catch (err) {
        if (err === BUSINESS_ACCOUNT_EXPIRED_INVITE_TOKEN_ERROR) {
            navigate(BUSINESS_ACCOUNT_EXPIRED_INVITATION_URL)
        } else {
            dispatch(setAuthError(err as string))
            setLoading(false)
            const errMessage =
                typeof err === 'string' ? err : 'Failed to signup'

            message.error(errMessage)
        }
    }
}

export const useGetPercent = ({
    value,
    options,
}: {
    value?: string
    options: any
}) =>
    useMemo(() => {
        if (
            !options ||
            !value ||
            value.length < PASSWORD_MIN ||
            value.length > PASSWORD_MAX
        ) {
            return 0
        }
        const result = zxcvbn(value, options)

        return (result?.score || 0) * 20
    }, [value, options])

export const useGetStrengthFeedbackAndStrokeColor = (percent: number) =>
    useMemo(
        () =>
            percent <= 40
                ? {
                      strengthFeedback: 'Weak',
                      strokeColor: getColor('error'),
                  }
                : percent <= 60
                ? {
                      strengthFeedback: 'Moderate',
                      strokeColor: getColor('warning'),
                  }
                : percent <= 80
                ? {
                      strengthFeedback: 'Good',
                      strokeColor: getColor('success'),
                  }
                : {
                      strengthFeedback: 'Strong',
                      strokeColor: getColor('success'),
                  },
        [percent],
    )

export const useLoadOptions = () =>
    useMemo(async () => {
        const zxcvbnCommonPackage = await import('@zxcvbn-ts/language-common')
        const zxcvbnEnPackage = await import('@zxcvbn-ts/language-en')

        return {
            translations: zxcvbnEnPackage.default.translations,
            dictionary: {
                ...zxcvbnCommonPackage.default.dictionary,
                ...zxcvbnEnPackage.default.dictionary,
            },
        }
    }, [])

export const getBusinessAccountInviteParams = (params: URLSearchParams) => {
    const businessAccountName = params.get(BUSINESS_ACCOUNT_NAME)
    const senderEmail = params.get(BUSINESS_ACCOUNT_SENDER_EMAIL)
    const senderName = params.get(BUSINESS_ACCOUNT_SENDER_NAME)
    const recipientEmail = params.get(BUSINESS_ACCOUNT_RECIPIENT_EMAIL)
    const businessAccountInviteToken = params.get(BUSINESS_ACCOUNT_INVITE_TOKEN)
    const businessAccountInviteExpireDate = params.get(
        BUSINESS_ACCOUNT_INVITE_EXPIRE_DATE,
    )

    return {
        businessAccountName: decodeURI(businessAccountName || ''),
        senderEmail: decodeURI(senderEmail || ''),
        recipientEmail: decodeURI(recipientEmail || ''),
        senderName: decodeURI(senderName || ''),
        businessAccountInviteExpireDate: decodeURIComponent(
            businessAccountInviteExpireDate || '',
        ),
        businessAccountInviteToken,
    }
}

export const inviteTokenIsExpired = (tokenExpires: string) => {
    const currentDate = new Date()
    const expireDate = new Date(tokenExpires)
    const isExpired = currentDate.getTime() >= expireDate.getTime()

    return isExpired
}
